import React, { useState } from "react";
import { KeyRound, Mail, Loader2, Check } from "lucide-react";
import api from "../../api";
import PasswordField from "../PasswordField";
import { useAuth } from "../../context/AuthContext";
import { useLang } from "../../context/LanguageContext";
import { useToast } from "../../hooks/use-toast";

const errText = (e, fallback) => {
  const d = e.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg).filter(Boolean).join(" ") || fallback;
  return d?.message || fallback;
};

export const AccountSecurityCard = () => {
  const { t } = useLang();
  const { user, refresh } = useAuth();
  const { toast } = useToast();
  const [open, setOpen] = useState("");
  const [busy, setBusy] = useState(false);
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [email, setEmail] = useState("");
  const [pending, setPending] = useState("");
  const [acceptWeak, setAcceptWeak] = useState(false);

  const reset = () => { setCur(""); setNext(""); setEmail(""); setOpen(""); };

  const savePassword = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/my/password", { currentPassword: cur, newPassword: next, acceptWeak });
      if (data.token) localStorage.setItem("lp-token", data.token);
      toast({ title: t("sec.pwChanged"), description: t("sec.pwChangedNote") });
      reset();
    } catch (err) { toast({ title: errText(err, t("acc.pleaseTryAgain")) }); }
    finally { setBusy(false); }
  };

  const saveEmail = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/my/email", { newEmail: email, currentPassword: cur });
      setPending(data.pending);
      toast({ title: t("sec.emailSent"), description: t("sec.emailSentNote", { email: data.pending }) });
      reset();
      refresh();
    } catch (err) { toast({ title: errText(err, t("acc.pleaseTryAgain")) }); }
    finally { setBusy(false); }
  };

  const btn = "text-xs font-bold border border-border hover:bg-secondary rounded-xl px-4 py-2.5 transition-colors inline-flex items-center gap-1.5";
  const save = "text-xs font-bold bg-[#16a34a] hover:bg-[#15803d] disabled:opacity-50 text-white rounded-xl px-4 py-2.5 transition-colors inline-flex items-center gap-1.5";
  const input = "w-full bg-secondary rounded-xl px-3 py-2.5 text-sm font-semibold text-foreground outline-none focus:ring-2 ring-[#16a34a]/40";

  return (
    <div className="bg-card border border-border rounded-2xl p-5" data-testid="account-security-card">
      <p className="text-base font-extrabold text-foreground">{t("sec.title")}</p>
      <p className="text-sm text-muted-foreground mt-1">{t("sec.sub")}</p>

      <div className="mt-4 pt-4 border-t border-border">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-sm font-bold text-foreground flex items-center gap-1.5"><KeyRound size={14} className="text-[#16a34a]" /> {t("sec.pwTitle")}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{t("sec.pwNote")}</p>
          </div>
          {open !== "pw" && (
            <button onClick={() => { reset(); setOpen("pw"); }} className={btn} data-testid="change-password-btn">{t("sec.pwCta")}</button>
          )}
        </div>
        {open === "pw" && (
          <form onSubmit={savePassword} className="mt-3 space-y-3" data-testid="change-password-form">
            <input type="password" autoComplete="current-password" value={cur} onChange={(e) => setCur(e.target.value)}
              placeholder={t("sec.currentPw")} className={input} data-testid="current-password-input" />
            <PasswordField value={next} onChange={(e) => setNext(e.target.value)} role={user?.role || "shopper"}
              placeholder={t("sec.newPw")} testid="new-password-input"
              acceptWeak={acceptWeak} onAcceptWeak={user?.role === "shopper" ? setAcceptWeak : undefined} />
            <div className="flex items-center gap-2">
              <button type="submit" disabled={busy || !cur || !next} className={save} data-testid="save-password-btn">
                {busy ? <Loader2 className="animate-spin" size={13} /> : <Check size={13} />} {t("sec.pwSave")}
              </button>
              <button type="button" onClick={reset} className="text-xs font-semibold text-muted-foreground hover:text-foreground px-2 py-2.5">{t("sl.customCancel")}</button>
            </div>
          </form>
        )}
      </div>

      <div className="mt-4 pt-4 border-t border-border">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-sm font-bold text-foreground flex items-center gap-1.5"><Mail size={14} className="text-[#16a34a]" /> {t("sec.emailTitle")}</p>
            <p className="text-xs text-muted-foreground mt-0.5" data-testid="current-email">{user?.email}</p>
          </div>
          {open !== "email" && (
            <button onClick={() => { reset(); setOpen("email"); }} className={btn} data-testid="change-email-btn">{t("sec.emailCta")}</button>
          )}
        </div>
        {pending && <p className="text-xs font-semibold text-[#16a34a] mt-2" data-testid="email-pending">{t("sec.emailPending", { email: pending })}</p>}
        {open === "email" && (
          <form onSubmit={saveEmail} className="mt-3 space-y-3" data-testid="change-email-form">
            <input type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder={t("sec.newEmail")} className={input} data-testid="new-email-input" />
            <input type="password" autoComplete="current-password" value={cur} onChange={(e) => setCur(e.target.value)}
              placeholder={t("sec.currentPw")} className={input} data-testid="email-password-input" />
            <p className="text-xs text-muted-foreground">{t("sec.emailConfirmNote")}</p>
            <div className="flex items-center gap-2">
              <button type="submit" disabled={busy || !email || !cur} className={save} data-testid="save-email-btn">
                {busy ? <Loader2 className="animate-spin" size={13} /> : <Check size={13} />} {t("sec.emailSave")}
              </button>
              <button type="button" onClick={reset} className="text-xs font-semibold text-muted-foreground hover:text-foreground px-2 py-2.5">{t("sl.customCancel")}</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default AccountSecurityCard;
